import type { GuideArticleSummary, GuideTopic } from '../types';

export type GuideLinkTarget = {
  /** A task or workflow article, matched on its slug (or stable id for bare slugs). */
  slug?: string;
  /** A topic; resolves to the article the catalog marks as its hub. */
  topic?: string;
  /** Used when the primary target has no published article. */
  fallbackSlug?: string;
};

export type ResolvedGuideLink = {
  article: GuideArticleSummary;
  topic: GuideTopic;
  /** The primary target had no published article and `fallbackSlug` resolved instead. */
  viaFallback: boolean;
};

/** Presence in the published index is the publication flag; stubs are placeholders, not guidance. */
const isLinkable = (article: GuideArticleSummary) => article.kind !== 'stub';

function findArticle(topics: GuideTopic[], slug: string): ResolvedGuideLink | undefined {
  for (const topic of topics) {
    const article = topic.articles.find(item => item.slug === slug || item.id === slug);
    if (article && isLinkable(article)) return { article, topic, viaFallback: false };
  }
  return undefined;
}

function findHub(topics: GuideTopic[], topicSlug: string): ResolvedGuideLink | undefined {
  const topic = topics.find(item => item.slug === topicSlug);
  const article = topic?.articles.find(item => item.kind === 'hub');
  return topic && article ? { article, topic, viaFallback: false } : undefined;
}

export function resolveGuideLinkTarget(
  topics: GuideTopic[] | undefined,
  target: GuideLinkTarget,
): ResolvedGuideLink | undefined {
  if (!topics) return undefined;
  const primary = target.slug
    ? findArticle(topics, target.slug)
    : target.topic
      ? findHub(topics, target.topic)
      : undefined;
  if (primary) return primary;
  const fallback = target.fallbackSlug ? findArticle(topics, target.fallbackSlug) : undefined;
  return fallback ? { ...fallback, viaFallback: true } : undefined;
}
